import { Pin } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { cn } from "@/utils/cn";
import {
  getParameterDisplayName,
  getParameterDescription,
} from "@/utils/parameterNames";
import {
  TooltipContent,
  TooltipTrigger,
  Tooltip as UITooltip,
} from "@/components/ui/tooltip";

interface KernelSettingsSectionProps {
  parameters: Record<string, number | [number, number] | boolean>;
  pinnedParameters: Record<string, boolean>;
}

// Kernel parameter keys in display order
const KERNEL_PARAMETERS = [
  "lookbackwindow",
  "relativeweight",
  "regressionlevel",
  "crossoverlag",
] as const;

// Get decimal precision based on parameter type
const getDecimalPrecision = (key: string): number => {
  if (key === "relativeweight") return 1;
  return 0;
};

// Format numeric value with appropriate precision
const formatNumericValue = (key: string, value: number | [number, number] | boolean): string => {
  // Handle boolean (shouldn't happen for kernel params, but defensive)
  if (typeof value === "boolean") {
    return value ? "On" : "Off";
  }
  const decimals = getDecimalPrecision(key);

  // Handle array (range) values
  if (Array.isArray(value)) {
    const isPinned = value.length === 2 && value[0] === value[1];
    const formatNum = (n: number) =>
      decimals > 0 ? n.toFixed(decimals) : Math.round(n).toString();

    if (isPinned) {
      return formatNum(value[0] ?? 0);
    }
    return `[${formatNum(value[0] ?? 0)}, ${formatNum(value[1] ?? 0)}]`;
  }

  // Handle single value
  return decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
};

export function KernelSettingsSection({
  parameters,
  pinnedParameters,
}: KernelSettingsSectionProps) {
  return (
    <div>
      <SectionHeader title="Kernel Settings" gradient="blue-cyan" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {KERNEL_PARAMETERS.map((key) => {
          const value = parameters[key];
          if (value === undefined) return null;

          const displayName = getParameterDisplayName(key);
          const description = getParameterDescription(key);
          const isPinned = pinnedParameters[key] === true;

          return (
            <UITooltip key={key}>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    "flex flex-col p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors",
                    isPinned && "border-amber-500/20"
                  )}
                >
                  <span className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                    {displayName}
                    {isPinned && (
                      <Pin className="h-3 w-3 text-amber-400" />
                    )}
                  </span>
                  <span className="text-sm font-mono font-medium">
                    {formatNumericValue(key, value)}
                  </span>
                </div>
              </TooltipTrigger>
              {description && (
                <TooltipContent aria-label={`${displayName} description`}>
                  <p className="max-w-xs">
                    {description}
                    {isPinned && (
                      <span className="block mt-1 text-amber-400 text-xs">
                        Pinned value
                      </span>
                    )}
                  </p>
                </TooltipContent>
              )}
            </UITooltip>
          );
        })}
      </div>
    </div>
  );
}
